import {
  AddPhotoAlternateOutlined,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  CloseOutlined,
  DeleteOutlined,
  SaveOutlined
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import {
  getDataFromCollection,
  getDocById,
  saveDocument,
  updateData,
} from '../../../MicrospecDynamicUI/Helpers/dataUtils';
import { deleteDoc, doc, setDoc } from 'firebase/firestore';
import uploadSingleFileToStorage from '../../../MicrospecDynamicUI/Helpers/saveFileToStorage';
import AccessDenied from '../../components/AccessDenied';
import PropertyAddressField from './PropertyAddressField';

// ─── Image Upload Thumbnail ─────────────────────────────────────────────────
const ImageThumbnail = ({ src, index, total, onRemove, onMoveLeft, onMoveRight }) => (
  <Box
    sx={{
      position: 'relative',
      width: 120,
      height: 120,
      borderRadius: 2,
      overflow: 'hidden',
      flexShrink: 0,
      border: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box
      component="img"
      src={src}
      alt={`property-${index}`}
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
    />
    <IconButton
      size="small"
      onClick={() => onRemove(index)}
      sx={{
        position: 'absolute',
        top: 4,
        right: 4,
        bgcolor: 'rgba(0,0,0,0.55)',
        color: '#fff',
        '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
        width: 26,
        height: 26,
      }}
    >
      <CloseOutlined sx={{ fontSize: 16 }} />
    </IconButton>
    <Box sx={{ position: 'absolute', bottom: 4, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.5 }}>
      {index > 0 && (
        <IconButton
          size="small"
          onClick={() => onMoveRight(index)}
          sx={{
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            width: 24,
            height: 24,
          }}
        >
          <ArrowForwardOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      )}
      {index < total - 1 && (
        <IconButton
          size="small"
          onClick={() => onMoveLeft(index)}
          sx={{
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
            width: 24,
            height: 24,
          }}
        >
          <ArrowBackOutlined sx={{ fontSize: 14 }} />
        </IconButton>
      )}
    </Box>
  </Box>
);

// ─── Section Wrapper ─────────────────────────────────────────────────────────
const FormSection = ({ title, children }) => (
  <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Paper>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const AddProperty = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);

  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { firestore, storage } = cloudProvider;
  const userProfile = cloudProvider.userProfile?.attributes;

  // ── Categories & Features ────────────────────────────────────────────────
  const [categories, setCategories] = useState([]);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [allFeatures, setAllFeatures] = useState([]);
  const [featuresLoaded, setFeaturesLoaded] = useState(false);

  // ── Form State ───────────────────────────────────────────────────────────
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [priceOnRequest, setPriceOnRequest] = useState(false);
  const [categoryId, setCategoryId] = useState('');
  const [address, setAddress] = useState('');
  const [addressDisplayName, setAddressDisplayName] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [features, setFeatures] = useState({});
  const [active, setActive] = useState(true);
  const [approvalStatus, setApprovalStatus] = useState(null);

  // ── Images ───────────────────────────────────────────────────────────────
  const [existingImages, setExistingImages] = useState([]); // URLs already in Firestore
  const [newImageFiles, setNewImageFiles] = useState([]); // File objects to upload
  const [newImagePreviews, setNewImagePreviews] = useState([]); // local preview URLs
  const fileInputRef = useRef(null);

  // ── UI State ─────────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loadingProperty, setLoadingProperty] = useState(isEdit);
  const [accessDenied, setAccessDenied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ── Fetch Categories & Features ──────────────────────────────────────────
  useEffect(() => {
    if (!firestore?.instance) return;

    getDataFromCollection(
      setCategories,
      (done) => { if (done) setCategoriesLoaded(true); },
      firestore.instance,
      'categories',
      true,
      50
    );

    getDataFromCollection(
      setAllFeatures,
      (done) => { if (done) setFeaturesLoaded(true); },
      firestore.instance,
      'features',
      true,
      100
    );
  }, [firestore?.instance]);

  // ── If editing, load existing property ───────────────────────────────────
  useEffect(() => {
    if (!isEdit || !firestore?.instance || !editId) return;

    getDocById(
      (property) => {
        if (!property) return;
        if (property.owner_id !== userProfile?.uid) {
          setLoadingProperty(false);
          setAccessDenied(true);
          return;
        }
        setTitle(property.title || '');
        setDescription(property.description || '');
        setPrice(property.price != null ? String(property.price) : '');
        setPriceOnRequest(property.price_on_request || false);
        setCategoryId(property.category_id || '');
        setAddress(property.address || '');
        setAddressDisplayName(property.address_display_name || '');
        setCoordinates(property.coordinates || null);
        setFeatures(property.features || {});
        setActive(property.active ?? true);
        setApprovalStatus(property.approval_status || null);
        setExistingImages(property.images || []);
      },
      (done) => { if (done) setLoadingProperty(false); },
      firestore.instance,
      'properties',
      editId,
      false
    );
  }, [isEdit, firestore?.instance, editId, navigate, userProfile?.uid]);

  // ── Derived ──────────────────────────────────────────────────────────────
  const booleanFeatures = useMemo(
    () => allFeatures.filter((f) => f.type === 'boolean'),
    [allFeatures]
  );
  const nonBooleanFeatures = useMemo(
    () => allFeatures.filter((f) => f.type !== 'boolean'),
    [allFeatures]
  );

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFeatureChange = (featureId, value) => {
    setFeatures((prev) => ({ ...prev, [featureId]: value }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const previews = files.map((f) => URL.createObjectURL(f));
    setNewImageFiles((prev) => [...prev, ...files]);
    setNewImagePreviews((prev) => [...prev, ...previews]);
    // Reset so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeNewImage = (index) => {
    setNewImagePreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    setNewImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const moveImage = (fromIndex, toIndex) => {
    const all = [
      ...existingImages.map((url) => ({ type: 'existing', url })),
      ...newImageFiles.map((file, i) => ({ type: 'new', file, preview: newImagePreviews[i] })),
    ];
    [all[fromIndex], all[toIndex]] = [all[toIndex], all[fromIndex]];

    const nextExisting = all.filter((x) => x.type === 'existing').map((x) => x.url);
    const nextFiles = all.filter((x) => x.type === 'new').map((x) => x.file);
    const nextPreviews = all.filter((x) => x.type === 'new').map((x) => x.preview);

    setExistingImages(nextExisting);
    setNewImageFiles(nextFiles);
    setNewImagePreviews(nextPreviews);
  };

  // ── Upload images ────────────────────────────────────────────────────────
  const uploadImages = async (propertyId) => {
    const urls = [];
    for (let i = 0; i < newImageFiles.length; i++) {
      const file = newImageFiles[i];
      const path = `properties/${propertyId}/${Date.now()}_${file.name}`;
      const url = await uploadSingleFileToStorage(storage, path, file);
      urls.push(url);
    }
    return urls;
  };

  // ── Validate ─────────────────────────────────────────────────────────────
  const validate = () => {
    if (!title.trim()) return 'يرجى إدخال عنوان العقار';
    if (!categoryId) return 'يرجى اختيار تصنيف العقار';
    if (!priceOnRequest && (!price || isNaN(Number(price)) || Number(price) < 0))
      return 'يرجى إدخال سعر صحيح أو تفعيل "السعر عند الطلب"';
    if (existingImages.length === 0 && newImageFiles.length === 0)
      return 'يرجى إضافة صورة واحدة على الأقل';
    return null;
  };

  // ── Save / Update ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setSnackbar({ open: true, message: error, severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const propertyData = {
        title: title.trim(),
        description: description.trim(),
        price: priceOnRequest ? 0 : Number(price),
        price_on_request: priceOnRequest,
        category_id: categoryId,
        owner_id: userProfile.uid,
        address: address.trim(),
        address_display_name: addressDisplayName.trim(),
        coordinates,
        features,
        active,
      };

      if (isEdit) {
        // Upload new images first
        const uploadedUrls = await uploadImages(editId);
        propertyData.images = [...existingImages, ...uploadedUrls];

        // Reset approval_status to under_review if it was declined
        if (approvalStatus === 'declined') {
          propertyData.approval_status = 'under_review';
        }

        await updateData(`properties/${editId}`, firestore, propertyData);
        const successMsg = approvalStatus === 'declined'
          ? 'تم تحديث العقار وإعادة تقديمه للمراجعة'
          : 'تم تحديث العقار بنجاح';
        setSnackbar({ open: true, message: successMsg, severity: 'success' });
        setTimeout(() => navigate('/owners/properties/manage'), 1200);
      } else {
        // Create the document first to get an ID
        propertyData.approval_status = 'under_review';
        propertyData.images = [];

        const result = await saveDocument(firestore, 'properties', propertyData);
        if (!result?.success) throw new Error('Failed to save document');

        // Upload images with the new doc ID, then update
        const docId = result.doc_id;
        const uploadedUrls = await uploadImages(docId);
        if (uploadedUrls.length > 0) {
          await updateData(`properties/${docId}`, firestore, { images: uploadedUrls });
        }

        setSnackbar({ open: true, message: 'تم إضافة العقار بنجاح وسيتم مراجعته', severity: 'success' });
        setTimeout(() => navigate('/owners/properties/manage'), 1200);
      }
    } catch (err) {
      console.error('Error saving property:', err);
      setSnackbar({ open: true, message: 'حدث خطأ أثناء الحفظ، يرجى المحاولة مرة أخرى', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ── Delete (soft) ────────────────────────────────────────────────────────
  const handleDelete = async () => {
    if (!window.confirm('هل أنت متأكد من حذف هذا العقار؟ لا يمكن التراجع عن هذا الإجراء.')) return;
    setDeleting(true);
    try {
      const db = firestore.instance;
      const propertyRef = doc(db, 'properties', editId);
      const deletedRef = doc(db, 'deleted_properties', editId);

      // Build the full property data from current form state
      const propertyData = {
        title: title.trim(),
        description: description.trim(),
        price: priceOnRequest ? 0 : Number(price),
        price_on_request: priceOnRequest,
        category_id: categoryId,
        owner_id: userProfile.uid,
        address: address.trim(),
        address_display_name: addressDisplayName.trim(),
        coordinates,
        features,
        active,
        images: existingImages,
        deleted_at: new Date(),
      };

      await setDoc(deletedRef, propertyData);
      await deleteDoc(propertyRef);

      setSnackbar({ open: true, message: 'تم حذف العقار بنجاح', severity: 'success' });
      setTimeout(() => navigate('/owners/properties/manage'), 1200);
    } catch (err) {
      console.error('Error deleting property:', err);
      setSnackbar({ open: true, message: 'حدث خطأ أثناء الحذف، يرجى المحاولة مرة أخرى', severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (loadingProperty || !categoriesLoaded || !featuresLoaded) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (accessDenied) {
    return (
      <AccessDenied
        message="لم يتم العثور على العقار المطلوب."
        backPath="/owners/properties/manage"
        backLabel="العودة إلى عقاراتي"
      />
    );
  }

  const allImages = [
    ...existingImages.map((url) => ({ type: 'existing', src: url })),
    ...newImagePreviews.map((url) => ({ type: 'new', src: url })),
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>
        {isEdit ? 'تعديل العقار' : 'إضافة عقار جديد'}
      </Typography>

      {isEdit && approvalStatus === 'declined' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          تم رفض هذا العقار. عدّل البيانات ثم احفظ لإعادة تقديمه للمراجعة تلقائيًا.
        </Alert>
      )}

      {/* Basic Info */}
      <FormSection title="المعلومات الأساسية">
        <TextField
          fullWidth
          label="عنوان العقار"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mb: 2 }}
        />
        <TextField
          fullWidth
          label="وصف العقار"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={4}
          sx={{ mb: 2 }}
        />
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>تصنيف العقار</InputLabel>
          <Select
            value={categoryId}
            label="تصنيف العقار"
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name || cat.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormSection>

      {/* Price */}
      <FormSection title="السعر">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <TextField
            label="السعر (₪)"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={priceOnRequest}
            sx={{ flex: 1 }}
            inputProps={{ min: 0 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={priceOnRequest}
                onChange={(e) => setPriceOnRequest(e.target.checked)}
              />
            }
            label="السعر عند الطلب"
          />
        </Box>
      </FormSection>

      {/* Images */}
      <FormSection title="صور العقار">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
          {allImages.map((img, i) => (
            <ImageThumbnail
              key={`${img.src}-${i}`}
              src={img.src}
              index={i}
              total={allImages.length}
              onRemove={(idx) => {
                if (idx < existingImages.length) {
                  removeExistingImage(idx);
                } else {
                  removeNewImage(idx - existingImages.length);
                }
              }}
              onMoveLeft={(idx) => moveImage(idx, idx + 1)}
              onMoveRight={(idx) => moveImage(idx, idx - 1)}
            />
          ))}
          <Box
            onClick={() => fileInputRef.current?.click()}
            sx={{
              width: 120,
              height: 120,
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'text.secondary',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
            }}
          >
            <AddPhotoAlternateOutlined sx={{ fontSize: 32, mb: 0.5 }} />
            <Typography variant="caption">إضافة صورة</Typography>
          </Box>
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleImageSelect}
        />
      </FormSection>

      {/* Location */}
      <FormSection title="الموقع">
        <PropertyAddressField
          address={address}
          setAddress={setAddress}
          addressDisplayName={addressDisplayName}
          setAddressDisplayName={setAddressDisplayName}
          coordinates={coordinates}
          setCoordinates={setCoordinates}
        />
      </FormSection>

      {/* Non-Boolean Features */}
      {nonBooleanFeatures.length > 0 && (
        <FormSection title="مواصفات العقار">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            {nonBooleanFeatures.map((feature) => (
              <TextField
                key={feature.id}
                label={feature.name}
                type="number"
                value={features[feature.id] ?? ''}
                onChange={(e) => handleFeatureChange(feature.id, e.target.value ? Number(e.target.value) : '')}
                InputProps={{
                  endAdornment: feature.suffix ? (
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      {feature.suffix}
                    </Typography>
                  ) : null,
                }}
                inputProps={{ min: 0 }}
              />
            ))}
          </Box>
        </FormSection>
      )}

      {/* Boolean Features */}
      {booleanFeatures.length > 0 && (
        <FormSection title="مميزات إضافية">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {booleanFeatures.map((feature) => {
              const isActive = features[feature.id] === true;
              return (
                <Chip
                  key={feature.id}
                  label={feature.name}
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'default'}
                  onClick={() => handleFeatureChange(feature.id, !isActive)}
                  sx={{ cursor: 'pointer' }}
                />
              );
            })}
          </Box>
        </FormSection>
      )}

      {/* Active Toggle (only in edit mode) */}
      {isEdit && (
        <FormSection title="حالة العقار">
          <FormControlLabel
            control={
              <Switch checked={active} onChange={(e) => setActive(e.target.checked)} />
            }
            label={active ? 'منشور' : 'مخفي'}
          />
        </FormSection>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {isEdit && (
          <Button
            variant="outlined"
            color="error"
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteOutlined />}
            onClick={handleDelete}
            disabled={saving || deleting}
            sx={{ mr: 'auto' }}
          >
            {deleting ? 'جارٍ الحذف...' : 'حذف العقار'}
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={() => navigate('/owners/dashboard')}
          disabled={saving || deleting}
        >
          إلغاء
        </Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlined />}
          onClick={handleSubmit}
          disabled={saving || deleting}
          sx={{ minWidth: 140 }}
        >
          {saving ? 'جارٍ الحفظ...' : isEdit && approvalStatus === 'declined' ? 'حفظ وإعادة تقديم' : isEdit ? 'حفظ التعديلات' : 'إضافة العقار'}
        </Button>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddProperty;
